/**
 * Member list — CBO-driven filter relevance + per-user field selection (TRUS-568).
 *
 * Pure helpers only (no React, no I/O) so the rules are unit-testable:
 *
 *  1. Which hardcoded "universal" filters (Language / Insurance / Line of
 *     Business / Plan) are relevant for this org. A universal filter that does
 *     not map onto the org's own data returns useless results — Maui's
 *     "Language" dropdown offered en/es/zh/... while the org records languages
 *     in its own `primary_language` field with Pacific-Islander options.
 *
 *  2. Each user's personalised view: which of the ORG-CONFIGURED list columns
 *     and advanced filters (member_field_configurations show_in_list /
 *     show_in_advanced_filter) they want on their own screen. The user can only
 *     narrow the org's set — never add a field the org did not configure.
 *
 * The selection is stored as the keys the user has HIDDEN, not the keys they
 * chose to show. That way a field an admin configures later appears for every
 * user by default (the org configuration stays the default), and a key whose
 * field the admin later removes is simply ignored.
 */

/** Minimal shape shared by MemberListColumn and MemberAdvancedFilter. */
export interface ConfiguredField {
  field_key: string;
  display_name?: string | null;
}

/** Page key under /api/user-filter-preferences that holds the field selection.
 *  Deliberately separate from the filter-values save ('member_list') so
 *  "Save my view" / "Reset saved view" and "Customize view" never overwrite
 *  each other. */
export const VIEW_FIELDS_PAGE_KEY = 'member_list_fields';

// ---------------------------------------------------------------------------
// 1. Relevance of the hardcoded filters
// ---------------------------------------------------------------------------

const LANGUAGE_KEY_RE = /(^|_)language$/i;
const LANGUAGE_LABEL_RE = /\blanguage\b/i;

/**
 * True when a configured field holds the member's language — `language`,
 * `preferred_language`, `primary_language`, `member_primary_language`, or any
 * field the org labelled with the word "Language" (e.g. "Member Primary
 * Language"). Keys like `language_notes` or `languages_spoken_count` do not
 * match on the key; they only match if the admin's label says "Language".
 */
export function isLanguageField(f: ConfiguredField | null | undefined): boolean {
  if (!f) return false;
  if (f.field_key && LANGUAGE_KEY_RE.test(f.field_key)) return true;
  return !!f.display_name && LANGUAGE_LABEL_RE.test(f.display_name);
}

/** The org has its own language field among the filters it configured. */
export function hasConfiguredLanguageFilter(defs: ConfiguredField[] | null | undefined): boolean {
  return (defs || []).some(isLanguageField);
}

export interface ProfileFieldLike {
  field_key: string;
  is_hidden?: boolean | null;
}

const INSURANCE_ID_KEYS = ['medicaid_id', 'medicare_id', 'commercial_insurance_id'];

/**
 * The hardcoded Insurance filter matches on the presence of medicaid_id /
 * medicare_id / commercial_insurance_id. It is only meaningful when the org
 * actually exposes at least one of those fields on its member profile. The
 * profile-fields endpoint lists every core field (visible by default), so an
 * unconfigured healthcare org keeps the filter; an org that hid all three
 * (Maui, IFR) does not get it.
 */
export function orgUsesInsuranceIds(profileFields: ProfileFieldLike[] | null | undefined): boolean {
  return (profileFields || []).some(
    (f) => INSURANCE_ID_KEYS.includes(f.field_key) && !f.is_hidden,
  );
}

export interface PlanLookupLike {
  lob_name?: string | null;
  product_name?: string | null;
}

/**
 * Sorted distinct non-empty values of one plan attribute across the org's
 * plan lookup (GET /api/orgs/{cbo}/members-plan-lookup, which is org-wide —
 * one row per member with an ACTIVE plan enrollment). Empty for any org with
 * no health-plan enrollments, which is the signal to hide LOB / Plan.
 */
export function distinctPlanValues(
  lookup: Record<number | string, PlanLookupLike> | null | undefined,
  attr: 'lob_name' | 'product_name',
  onlyLob?: string,
): string[] {
  const out = new Set<string>();
  for (const p of Object.values(lookup || {})) {
    if (!p) continue;
    if (onlyLob && p.lob_name !== onlyLob) continue;
    const v = p[attr];
    if (typeof v === 'string' && v.trim()) out.add(v);
  }
  return Array.from(out).sort();
}

// ---------------------------------------------------------------------------
// 2. Per-user field selection
// ---------------------------------------------------------------------------

export interface ViewFieldSelection {
  /** field_keys of org-configured list columns this user has hidden. */
  hidden_columns: string[];
  /** field_keys of org-configured advanced filters this user has hidden. */
  hidden_filters: string[];
}

export const EMPTY_SELECTION: ViewFieldSelection = { hidden_columns: [], hidden_filters: [] };

function stringList(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const x of v) {
    if (typeof x === 'string' && x && !out.includes(x)) out.push(x);
  }
  return out;
}

/** Read a selection out of a stored filter_state blob, tolerating anything. */
export function parseSelection(raw: unknown): ViewFieldSelection {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_SELECTION };
  const r = raw as Record<string, unknown>;
  return {
    hidden_columns: stringList(r.hidden_columns),
    hidden_filters: stringList(r.hidden_filters),
  };
}

/** The org's configured fields minus the ones this user hid, in org order.
 *  Hidden keys that no longer exist in the org config are ignored. */
export function applySelection<T extends ConfiguredField>(orgFields: T[], hiddenKeys: string[]): T[] {
  if (!hiddenKeys.length) return orgFields;
  const hidden = new Set(hiddenKeys);
  return orgFields.filter((f) => !hidden.has(f.field_key));
}

/** Drop hidden keys the org no longer configures, so a saved selection never
 *  accumulates stale ids. */
export function pruneSelection(
  sel: ViewFieldSelection,
  orgColumns: ConfiguredField[],
  orgFilters: ConfiguredField[],
): ViewFieldSelection {
  const colKeys = new Set(orgColumns.map((c) => c.field_key));
  const filterKeys = new Set(orgFilters.map((f) => f.field_key));
  return {
    hidden_columns: sel.hidden_columns.filter((k) => colKeys.has(k)),
    hidden_filters: sel.hidden_filters.filter((k) => filterKeys.has(k)),
  };
}

/** True when the selection hides nothing that the org currently configures. */
export function isOrgDefault(
  sel: ViewFieldSelection,
  orgColumns: ConfiguredField[],
  orgFilters: ConfiguredField[],
): boolean {
  const p = pruneSelection(sel, orgColumns, orgFilters);
  return p.hidden_columns.length === 0 && p.hidden_filters.length === 0;
}
